import { IUserMetadata } from '../authentication/interfaces/AuthInterface';
import _ from 'lodash';
import AuthService from '../authentication/services/AuthService';
import { DispatcherManager } from '../Dispatcher';
import { 
  Operation, 
  OperationType, 
  Position, 
  RequestData 
} from '../Types';
import uuid from 'uuid-random';
import { 
  REQUEST_CONFIG_BASE_PROPS 
} from '../../components/UI/fields/multiselect/Types';
import { spliceArray } from '../../utility';

export type SocketPingData = {
  id: string;
  data: any;
}

export default class Socket {
  private baseUrl: string = '';
  private subscriptionUrlTemplate: string = '';
  private tokenName: string = '';
  private dispatcher: DispatcherManager;
  private stompClient: any = null;
  private authUser: any;
  private sessionOperations: Map<string, Operation[]> = new Map();
  private _connected;
  private subscriptionUrlSuffix: string = '';
  private dynamicSubscriptionByUser: boolean = false;

  get connected() {
    return this._connected;
  }

  set connected(isConnected) {
    this._connected = isConnected;
  }

  constructor(config, dispatcher) {
    this.dispatcher = dispatcher;
    const { URL, SUBSCRIPTION_URL_TEMPLATE, TOKEN_NAME = 'auth_token', DYNAMIC_SUBSCRIPTION_BY_USER = false } = config;
    this.baseUrl = URL;
    this.subscriptionUrlTemplate = SUBSCRIPTION_URL_TEMPLATE;
    this.tokenName = TOKEN_NAME;
    this.dynamicSubscriptionByUser = DYNAMIC_SUBSCRIPTION_BY_USER;
  }

  /**
   * Connets with socket server
   */
  public connect = () => {
    let token = '';
    if (AuthService.isRunning()) {
      AuthService.getUser().then((user: IUserMetadata | null | any) => {
        if (user) {
          this.authUser = user;
          token = this.authUser.access_token;
          this.connectStompClient(token);
        }
      }).catch(e => e);
    }
    if (!token && this.dispatcher.appOperationData.token) {
      token = this.dispatcher.appOperationData.token;
      this.connectStompClient(token);
    }
  }

  /**
   * Connects with stomp client
   * @param {String} token Authorization  token
   */
  private connectStompClient = (token: string) => {
    if (this.baseUrl) {
      const Stomp = require('stompjs');
      const SockJS = require('sockjs-client');
      const webSocketUrl = this.baseUrl;
      const url = webSocketUrl + `?${this.tokenName}=` + token;
      const SockJSInstance = new SockJS(url);
      this.stompClient = Stomp.over(SockJSInstance);
      this.stompClient.heartbeat.outgoing = 60000;
      this.stompClient.heartbeat.incoming = 60000;
      this.stompClient.connect(new Map(), this.onConnected.bind(this, this.stompClient), this.onError.bind(this));
    } else {
      this.dispatcher.toDispatcher();
    }
  }

  /**
   * Successful connection to backend with stomp client and subscription.
   * @param {any} data
   * @param {any} connection
   */
  private onConnected = (data: any, connection: any) => {
    if (this.dynamicSubscriptionByUser) {
      const wsURL = data.ws._transport.ws.url;
      const startIndex = wsURL.search(/\/\d{3}\/\w{8}\/websocket/i);
      const sessionId = startIndex === -1 ? '' : wsURL.substring(startIndex + 5, startIndex + 13);
      if (new RegExp(/\w{8}/).test(sessionId)) {
        if (!this.subscriptionUrlSuffix) {
          this.subscriptionUrlSuffix = `-user${sessionId}`;
        }
      } else {
        this.onError(wsURL);
      }
    }
    let subscriptionURL = `${this.subscriptionUrlTemplate}${this.subscriptionUrlSuffix}`;
    if (connection?.headers?.server) {
      const userName = connection.headers['user-name'].replace('@transperfect.com', '');
      subscriptionURL = subscriptionURL.replace('{{username}}', userName);
      this.subscribe(subscriptionURL);
    } else {
      this.subscribe(subscriptionURL);
    }
  }

  /**
   * Subscribes to the channel
   * @param {String} subscriptionURL
   */
  private subscribe = (subscriptionURL: string) => {
    this.stompClient.subscribe(
      subscriptionURL,
      this.onSocketPing.bind(this),
      { id: subscriptionURL }
    );
    this.dispatcher.toDispatcher();
    this._connected = true;
  }

  /**
   * Error handler for connect to ws.
   * @param err
   */
  private onError = (err: any) => {
    console.log(err);
    this.dispatcher.toDispatcher();
  }
  /**
   * Listener of socket ping
   * @param {any} data
   */
  private onSocketPing = (data: any = {}) => {
    const msg = JSON.parse(data?.body);
    if (msg?.id) {
      const socketOperations = this.sessionOperations.get(msg.id);
      if (!_.isEmpty(socketOperations)) {
        // const dispatcherId = `socketDispatcherId_${msg?.id}`;
        let operations = [{
          data: {
            socketId: msg?.id,
            data: msg?.data
          },
          type: OperationType.data
        }, ...socketOperations
        // , {
        //   actions: ['all'],
        //   items: ['all'],
        //   type: OperationType.syncDispatchersDataItem
        // },
        // {
        //   dispatcherIds: [dispatcherId],
        //   type: OperationType.unlink
        ];

        const actionName = `socketAction_${msg?.id}`;
        _.set(this.dispatcher.appActions, actionName, [...operations]);
        this.dispatcher.dispatch(actionName, {
          pos: Position.end,
          stack: true // ,
          // dispatcherId
        });
      }
    }
  }
  /**
   * Sets session operation by socket id
   * @param {Operation[]} operations Dispatcher operations
   * @param {String} socketId Id of socket session
   * @param {RequestData} requestConfig
   *
   */
  setSessionOperations = (operations: Operation[], socketId?: string, requestConfig?: RequestData) => {
    if (socketId) {
      this.sessionOperations.set(socketId, _.cloneDeep(operations));
      this.dispatcher.toDispatcher();
    } else if (!_.isEmpty(requestConfig)) {
      socketId = uuid();
      this.sessionOperations.set(socketId, _.cloneDeep(operations));
      requestConfig = this.dispatcher.handleTemplatingAndUpdatingData(requestConfig);
      if (_.isEmpty(requestConfig.method)) {
        requestConfig.method = 'post';
      }
      if (_.isEmpty(requestConfig.params)) {
        requestConfig.params = {};
      }
      requestConfig.params.id = socketId;
      let clientOperations = [{
        data: { ...requestConfig },
        type: OperationType.request
      }];
      spliceArray(this.dispatcher.appExecutedOperations, clientOperations, 1, 0);
      this.dispatcher.toDispatcher();
    }
  }
  /**
   * It triggers socket operations by id
   * @param {SocketPingData} socketPingData
   */
  fakePingOperations = (socketPingData: SocketPingData) => {
    const dispatcherId = `socketDispatcherId_${socketPingData?.id}`;
    let operations = [{
      data: {
        socketId: socketPingData?.id,
        data: socketPingData?.data
      },
      type: OperationType.data
    }, ...this.sessionOperations.get(socketPingData.id), {
      dispatcherIds: [dispatcherId],
      type: OperationType.unlink
    }];
    const actionName = `socketAction_${socketPingData?.id}`;
    _.set(this.dispatcher.appActions, actionName, [...operations]);
    // spliceArray(this.dispatcher.appExecutedOperations, [...operations], this.dispatcher.appExecutedOperations.length, 0);
    // this.dispatcher.toDispatcher(true);
    this.dispatcher.dispatch(actionName, {
      pos: Position.end,
      stack: true,
      dispatcherId
    });
  }
}
